import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import HeroCarousel from '../components/HeroCarousel';
import ContactForm from '../components/ContactForm';
import PlanosAvulsos from '../components/PlanosAvulsos';
import FaqSection from '../components/FaqSection';
import Testimonials from '../components/Testimonials';
import Footer from '../components/Footer';
import './HomePage.css';
import './ContactSection.css';

const HOME_WHATSAPP_URL = 'https://wa.me/5521987940668?text=Ola!%20Quero%20consultar%20a%20viabilidade%20de%20internet%20para%20o%20meu%20endereco.';

const IconPix = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 7h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
    <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
    <path d="M8.5 12h7" />
    <path d="M12 9.5v5" />
  </svg>
);

const IconKey = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8.5" cy="14.5" r="3.5" />
    <path d="M11 12l7-7" />
    <path d="M17 5h2v2" />
    <path d="M14.5 8.5l1 1" />
  </svg>
);

const IconClipboard = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="12" height="16" rx="2" />
    <path d="M9 4.5h6" />
    <path d="M9 10h6" />
    <path d="M9 14h6" />
    <path d="M9 18h4" />
  </svg>
);

const IconUserEdit = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="8" r="3.5" />
    <path d="M4 20c.6-3.4 3.2-5.5 6-5.5.9 0 1.7.2 2.5.6" />
    <path d="m17.4 14.6 3 3-4.2 4.2H13v-3.2z" />
  </svg>
);

const QUICK_ACTIONS = [
  { id: 'pix', label: 'Pague com Pix', description: 'Acesse sua cobrança e quite pelo QR Code.', icon: IconPix, to: '/2via' },
  { id: 'senha', label: 'Troca de Senha', description: 'Solicite alteração da senha do Wi-Fi ou app.', icon: IconKey, to: '/solicitacoes?acao=troca-senha' },
  { id: 'os', label: 'Consulta de O.S', description: 'Veja o andamento dos chamados já abertos.', icon: IconClipboard, to: '/solicitacoes?acao=consultar-chamados' },
  { id: 'cadastro', label: 'Atualização Cadastral', description: 'Atualize telefone, e-mail e endereço.', icon: IconUserEdit, to: '/solicitacoes?acao=atualizacao-cadastral' },
];

function HomePage() {
  const heroMsaRef = useRef(null);
  const planosRef = useRef(null);
  const contactRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const renderQuickActions = (cardClassName = 'home-quick-actions__card') => (
    <div className="home-quick-actions">
      <div className="home-quick-actions__track">
        {QUICK_ACTIONS.map((action) => {
          const Icon = action.icon;
          return (
            <Link key={action.id} to={action.to} className={cardClassName}>
              <span className="home-quick-actions__icon"><Icon /></span>
              <span className="home-quick-actions__body">
                <strong className="home-quick-actions__label">{action.label}</strong>
                <span className="home-quick-actions__description">{action.description}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );

  // Rola até o FAQ quando a Home é aberta a partir do link FAQ do navbar em outra página
  useEffect(() => {
    if (location.state?.scrollTo === 'faq') {
      const timer = window.setTimeout(() => {
        document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        navigate(location.pathname, { replace: true, state: {} });
      }, 150);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    if (heroMsaRef.current) observer.observe(heroMsaRef.current);
    if (planosRef.current) observer.observe(planosRef.current);
    if (contactRef.current) observer.observe(contactRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div>
      {/* Seção apresentação - Banner Faynet */}
      <section className="hero-msa" ref={heroMsaRef}>
        <div className="hero-msa__banner">
          <HeroCarousel />
        </div>
      </section>

      <section className="home-mobile-actions" aria-label="Acessos frequentes">
        <div className="home-mobile-actions__faq-band">
          <div className="home-mobile-actions__faq-inner">
            <span className="home-mobile-actions__eyebrow">Buscas frequentes</span>
          </div>
        </div>

        <div className="home-mobile-actions__inner">
          {renderQuickActions('home-quick-actions__card home-quick-actions__card--mobile')}
        </div>
      </section>

      {/* Seção Planos de internet avulsa */}
      <section className="home-planos" id="home-planos" ref={planosRef}>
        <div className="home-planos__cta">
          <div className="home-planos__cta-top">
            <p className="home-planos__cta-text">Precisa de ajuda ou tem alguma dúvida? Fale com um de nossos especialistas</p>
            <a
              href={HOME_WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="home-planos__cta-btn"
            >
              CHAMAR NO WHATSAPP
            </a>
          </div>

          {renderQuickActions()}
        </div>
        <div className="home-planos__header">
          <span className="home-planos__eyebrow">Planos de internet</span>
          <h2 className="home-planos__title">Escolha o plano ideal para você</h2>
          <p className="home-planos__subtitle">
            Temos o plano certo para o seu perfil, com velocidade, estabilidade e o melhor custo-beneficio.
          </p>
        </div>
        <div className="home-planos__wrapper">
          <PlanosAvulsos showViewAllLink mobileShowcase />
        </div>
      </section>

      {/* Depoimentos de Clientes */}
      <Testimonials />

      {/* Seção Fale Conosco */}
      <section className="contact-section" ref={contactRef}>
        <div className="contact-section__wrapper">
          {/* Lado esquerdo - Formulário */}
          <div className="contact-section__left">
            <div className="contact-section__header">
              <h2 className="contact-section__title">Fale com a Faynet</h2>
              <p className="contact-section__subtitle">
                Dúvidas, planos, parcerias, reclamacoes ou qualquer outro assunto: preencha o formulário e nossa equipe entrará em contato em até 24 horas.
              </p>
            </div>
            <ContactForm />
          </div>

          {/* Lado direito - Imagem Notebook */}
          <div className="contact-section__right">
            <img 
              src="/index/inicio3.png" 
              alt="Sistema MSA Porta Paletes - Tecnologia e Inovação" 
              className="contact-section__image"
            />
          </div>
        </div>
      </section>

      {/* Perguntas frequentes */}
      <FaqSection />

      <Footer />
    </div>
  );
}

export default HomePage;
